import { validateEmail } from '@/utils/emailUtils'
import { isLocationCreatable } from '@/utils/isLocationCreatable'
import { isTaxRatePercentValid } from '@/utils/taxRateUtils'

import {
  type CreateOrgFormValues,
  DISPLAY_NAME_MAX_LENGTH,
  ORG_NAME_MIN_LENGTH,
  TEMPORARY_PASSWORD_MIN_LENGTH,
} from '../definitions/createOrganizationForm'

/**
 * Whether the admin user can be created.
 *
 * The four identity fields and the temporary password are the endpoint's entire
 * required set. NPI, license number, and the primary-location assignment are
 * optional and never gate — they are details a practice can fill in later, and
 * none of them is something CS reliably has at signup.
 */
const isAdminCreatable = (values: CreateOrgFormValues): boolean =>
  values.admin_name_given.trim().length > 0 &&
  values.admin_name_family.trim().length > 0 &&
  values.admin_username.trim().length > 0 &&
  validateEmail(values.admin_email) === '' &&
  values.admin_temporary_password.length >= TEMPORARY_PASSWORD_MIN_LENGTH

/**
 * Whether each wizard step is complete enough to leave, indexed by step.
 *
 * Basic Information, Location, and Admin User carry the required fields. Tax is
 * entirely optional, so it blocks only on a malformed rate. Review collects
 * nothing.
 *
 * None of the three records can be waved past: an organization with no location
 * has no address, phone, or time zone, and one nobody can sign in to is not a
 * finished setup. A run that has to stop before the end leaves the organization
 * resumable rather than leaving a record unwritten on purpose.
 */
export const getStepValidity = (values: CreateOrgFormValues): boolean[] => {
  const orgName = values.org_name.trim()
  const displayName = values.display_name.trim()

  return [
    orgName.length >= ORG_NAME_MIN_LENGTH &&
      displayName.length > 0 &&
      displayName.length <= DISPLAY_NAME_MAX_LENGTH,
    isTaxRatePercentValid(values.tax_rate),
    isLocationCreatable(values),
    isAdminCreatable(values),
    true,
  ]
}
